import React, { useMemo, useState, useEffect } from "react"
import TableContainer from "components/Common/TableContainer"
import {
  CardBody,
  Card,
  Row,
  Col,
  Form,
  Offcanvas,
  OffcanvasBody,
  OffcanvasHeader,
  Label,
  Input,
  Container,
  FormFeedback,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
} from "reactstrap"
import { useFormik } from "formik"
import * as Yup from "yup"
import Breadcrumbs from "../../../components/Common/Breadcrumb"

// Redux
import { useDispatch, useSelector } from "react-redux"
import {
  addPayment,
  deletePayment,
  fetchPayments,
  resetPaymentState,
  updatePayment,
} from "toolkitStore/ticketMSlice/TicketVenueSlice"

import APIServerIP from "helpers/Sats_api_helper"

function PaymentTypes() {
  const dispatch = useDispatch()

  const { paymentList, paymentIsSuccessfully } = useSelector(
    state => state.TicketVenueReducer
  )

  const [isRight, setIsRight] = useState(false)
  const [selectedData, setSelectedData] = useState(null)
  const [modalOpen, setModalOpen] = useState(false)
  const [deleteData, setDeleteData] = useState(null)

  const toggleRightCanvas = () => setIsRight(!isRight)
  const toggleDeleteModal = () => setModalOpen(!modalOpen)

  /* ---------------- Fetch Payments ---------------- */

  useEffect(() => {
    dispatch(fetchPayments())
  }, [dispatch])

  /* ---------------- Success Reset ---------------- */

  useEffect(() => {
    if (paymentIsSuccessfully) {
      dispatch(fetchPayments())
      setIsRight(false)
      setSelectedData(null)
      dispatch(resetPaymentState())
    }
  }, [paymentIsSuccessfully, dispatch])

  /* ---------------- Table Columns ---------------- */

  const columns = useMemo(
    () => [
      {
        Header: "Payment Name",
        accessor: "payment_name",
      },
      {
        Header: "Status",
        accessor: "enabled",
        Cell: ({ row }) => (
          <span
            className={`badge ${
              row.original.enabled ? "bg-success" : "bg-danger"
            }`}
          >
            {row.original.enabled ? "Enabled" : "Disabled"}
          </span>
        ),
      },
      {
        Header: "Action",
        Cell: ({ row }) => (
          <div className="d-flex gap-2">
            <Button
              color="info"
              size="sm"
              onClick={() => {
                setSelectedData(row.original)
                setIsRight(true)
              }}
            >
              Edit
            </Button>

            <Button
              color="danger"
              size="sm"
              onClick={() => {
                setDeleteData(row.original)
                setModalOpen(true)
              }}
            >
              Delete
            </Button>
          </div>
        ),
      },
    ],
    []
  )

  /* ---------------- Formik ---------------- */

  const validation = useFormik({
    enableReinitialize: true,

    initialValues: {
      payment_name: selectedData?.payment_name || "",
      enabled: selectedData?.enabled || false,
      clientId: "",
      clientSecret: "",
      publishableKey: "",
      secretKey: "",
    },

    validationSchema: Yup.object({
      payment_name: Yup.string().required("Payment name is required"),
    }),

    onSubmit: async values => {
      try {
        const dbPayload = {
          _id: selectedData?._id,
          payment_name: values.payment_name,
          enabled: values.enabled,
        }

        if (selectedData) {
          await dispatch(
            updatePayment(dbPayload, {
              paypalClientId:
                values.payment_name === "paypal" ? values.clientId : "",

              paypalSecret:
                values.payment_name === "paypal" ? values.clientSecret : "",

              stripePublishable:
                values.payment_name === "stripe" ? values.publishableKey : "",

              stripeSecret:
                values.payment_name === "stripe" ? values.secretKey : "",
            })
          )
        } else {
          dispatch(
            addPayment(dbPayload, {
              paypalClientId:
                values.payment_name === "paypal" ? values.clientId : "",

              paypalSecret:
                values.payment_name === "paypal" ? values.clientSecret : "",

              stripePublishable:
                values.payment_name === "stripe" ? values.publishableKey : "",

              stripeSecret:
                values.payment_name === "stripe" ? values.secretKey : "",
            })
          )
        }
      } catch (error) {
        console.error("Payment API error:", error)
      }
    },
  })

  /* ---------------- Load ENV Keys when Editing ---------------- */

  useEffect(() => {
    if (!selectedData) return

    fetch(`${APIServerIP}manage-ticket-event-venue/public/payment-config`)
      .then(res => res.json())
      .then(data => {
        if (selectedData.payment_name === "paypal") {
          validation.setFieldValue("clientId", data.paypalClientId || "")
          validation.setFieldValue("clientSecret", data.paypalSecret || "")
        }

        if (selectedData.payment_name === "stripe") {
          validation.setFieldValue(
            "publishableKey",
            data.stripePublishable || ""
          )
          validation.setFieldValue("secretKey", data.stripeSecret || "")
        }
      })
      .catch(err => console.log("Payment config error", err))
  }, [selectedData])

  /* ---------------- Delete Payment ---------------- */

  const handleDelete = async () => {
    try {
      await dispatch(deletePayment(deleteData._id))

      setDeleteData(null)

      toggleDeleteModal()
    } catch (error) {
      console.error("Delete payment error:", error)
    }
  }

  const hasPaypal = paymentList.some(
    p => p.payment_name?.toLowerCase() === "paypal"
  )

  const hasStripe = paymentList.some(
    p => p.payment_name?.toLowerCase() === "stripe"
  )

  const canAddMore = !(hasPaypal && hasStripe)

  /* ---------------- UI ---------------- */

  return (
    <Container fluid className="page-content">
      <Breadcrumbs title="" breadcrumbItem="Payment Management" />

      <Row>
        <Card>
          <CardBody>
            <div className="d-flex align-items-center border-bottom mb-3">
              <h5 className="mb-1 card-title flex-grow-1 text-primary">
                Payment Methods
              </h5>

              
              {canAddMore && (
                <Button
                  color="primary"
                  onClick={() => {
                    setSelectedData(null)
                    validation.resetForm()
                    setIsRight(true)
                  }}
                >
                  Add New Payment
                </Button>
              )}
            </div>

            <TableContainer
              columns={columns}
              data={paymentList}
              isGlobalFilter
              customPageSize={10}
            />
          </CardBody>
        </Card>
      </Row>

      {/* ---------------- Add/Edit Panel ---------------- */}

      <Offcanvas
        isOpen={isRight}
        scrollable
        direction="end"
        toggle={toggleRightCanvas}
      >
        <OffcanvasHeader toggle={toggleRightCanvas}>
          {selectedData ? "Edit Payment" : "Add Payment"}
        </OffcanvasHeader>

        <OffcanvasBody>
          <Form
            onSubmit={e => {
              e.preventDefault()
              validation.handleSubmit()
            }}
          >
            <Row className="p-3">
              <Col md={12}>
                <div className="mb-3">
                  <Label>Payment Name</Label>

                  <Input
                    type="select"
                    name="payment_name"
                    value={validation.values.payment_name}
                    onChange={validation.handleChange}
                    disabled={!!selectedData}
                  >
                    <option value="">Select Payment</option>

                    {(!hasStripe ||
                      selectedData?.payment_name === "stripe") && (
                      <option value="stripe">Stripe</option>
                    )}

                    {(!hasPaypal ||
                      selectedData?.payment_name === "paypal") && (
                      <option value="paypal">PayPal</option>
                    )}
                  </Input>

                  {validation.touched.payment_name &&
                    validation.errors.payment_name && (
                      <FormFeedback>
                        {validation.errors.payment_name}
                      </FormFeedback>
                    )}
                </div>

                <div className="mb-3">
                  <Label>Enable Payment</Label>

                  <Input
                    type="checkbox"
                    checked={validation.values.enabled}
                    onChange={e =>
                      validation.setFieldValue("enabled", e.target.checked)
                    }
                  />
                </div>

                {/* PayPal Fields */}

                {(validation.values.payment_name || "").toLowerCase() ===
                  "paypal" && (
                  <>
                    <div className="mb-3">
                      <Label>Client ID</Label>

                      <Input
                        name="clientId"
                        placeholder="PayPal Client ID"
                        value={validation.values.clientId}
                        onChange={validation.handleChange}
                      />
                    </div>

                    <div className="mb-3">
                      <Label>Client Secret</Label>

                      <Input
                        name="clientSecret"
                        placeholder="PayPal Client Secret"
                        value={validation.values.clientSecret}
                        onChange={validation.handleChange}
                      />
                    </div>
                  </>
                )}

                {/* Stripe Fields */}

                {(validation.values.payment_name || "").toLowerCase() ===
                  "stripe" && (
                  <>
                    <div className="mb-3">
                      <Label>Publishable Key</Label>

                      <Input
                        name="publishableKey"
                        placeholder="pk_test_******"
                        value={validation.values.publishableKey}
                        onChange={validation.handleChange}
                      />
                    </div>

                    <div className="mb-3">
                      <Label>Secret Key</Label>

                      <Input
                        name="secretKey"
                        placeholder="sk_test_******"
                        value={validation.values.secretKey}
                        onChange={validation.handleChange}
                      />
                    </div>
                  </>
                )}
              </Col>

              <Col className="col-12 mt-3 d-flex gap-2">
                <Button color="danger" onClick={toggleRightCanvas}>
                  Cancel
                </Button>

                <Button type="submit" color="primary">
                  Save
                </Button>
              </Col>
            </Row>
          </Form>
        </OffcanvasBody>
      </Offcanvas>

      {/* ---------------- Delete Modal ---------------- */}

      <Modal isOpen={modalOpen} toggle={toggleDeleteModal}>
        <ModalHeader toggle={toggleDeleteModal}>Delete Payment</ModalHeader>

        <ModalBody>
          Are you sure you want to delete
          <strong> {deleteData?.payment_name}</strong> ?
        </ModalBody>

        <ModalFooter>
          <Button color="danger" onClick={handleDelete}>
            Yes, Delete
          </Button>

          <Button color="secondary" onClick={toggleDeleteModal}>
            Cancel
          </Button>
        </ModalFooter>
      </Modal>
    </Container>
  )
}

export default PaymentTypes
